/**
 * Page-agnostic: must not import from `configs/<page>/`.
 */
import type { DaySchedule, TimeRange, WeekSchedule } from "@/types/scheduling";
import { DAY_CODES } from "@/constants/scheduling.constants";
import { isArray, isBoolean, isObject, isString } from "@/lib/utils/typeof";

const isTimeRange = (value: unknown): value is TimeRange =>
  isObject<TimeRange>(value) && isString(value.start) && isString(value.end);

const isDaySchedule = (value: unknown): value is DaySchedule =>
  isObject<DaySchedule>(value) &&
  isBoolean(value.enabled) &&
  isArray(value.ranges) &&
  value.ranges.every(isTimeRange);

/**
 * Structural, not a cast: `ConfigFieldDispatcher` hands every primitive only
 * the type it can render, and this is the one field whose value is a shape
 * rather than a scalar.
 */
export const isWeekSchedule = (value: unknown): value is WeekSchedule =>
  isObject<Record<string, unknown>>(value) &&
  DAY_CODES.every((day) => isDaySchedule(value[day]));

/**
 * Whether any day that is on holds more ranges than the field can show. The
 * editor would drop the extra ones on save, so such a week is shown
 * read-only instead.
 */
export const hasDayOverRangeLimit = (
  schedule: WeekSchedule,
  rangesPerDay: number
): boolean =>
  DAY_CODES.some(
    (day) => schedule[day].enabled && schedule[day].ranges.length > rangesPerDay
  );

/**
 * A lunch break is what a second range is for, so adding one splits the
 * day around this hour rather than copying the first range, which would
 * overlap it at once.
 */
const BREAK_START = "13:00";
const BREAK_END = "14:00";
/**
 * The gap and the length of a second range added beside a range that does
 * not span the lunch hour.
 */
const ADDED_RANGE_GAP_MINUTES = 60;
const ADDED_RANGE_MINUTES = 60;
const MINUTES_PER_DAY = 24 * 60;

/**
 * `HH:mm` as minutes since midnight. An end of `00:00` is midnight at the
 * end of the day.
 */
const toMinutes = (time: string, isEnd = false): number => {
  const [hours, minutes] = time.split(":").map(Number);
  const total = hours * 60 + minutes;

  return isEnd && total === 0 ? MINUTES_PER_DAY : total;
};

const toTime = (minutes: number): string =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

/**
 * The day's one range as two that do not overlap. A range across the lunch
 * hour is split around it. Any other gets a short range after it. The
 * user's range always stays first, so "remove the second range" removes
 * the added one. `undefined` when the range is invalid (the added one
 * would be built from a wrong end) or the day has no room after it.
 */
export const splitRangeForBreak = (
  range: TimeRange
): TimeRange[] | undefined => {
  const start = toMinutes(range.start);
  const end = toMinutes(range.end, true);

  if (!(start < end)) {
    return undefined;
  }

  if (start < toMinutes(BREAK_START) && end > toMinutes(BREAK_END)) {
    return [
      { start: range.start, end: BREAK_START },
      { start: BREAK_END, end: range.end },
    ];
  }

  const laterStart = end + ADDED_RANGE_GAP_MINUTES;
  if (laterStart + ADDED_RANGE_MINUTES < MINUTES_PER_DAY) {
    return [
      range,
      {
        start: toTime(laterStart),
        end: toTime(laterStart + ADDED_RANGE_MINUTES),
      },
    ];
  }

  return undefined;
};
